import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface VideoContextType {
    currentlyPlayingId: string | null;
    visiblePostId: string | null; // The post with video that is currently visible
    registerVideo: (id: string, pauseCallback: () => void) => void;
    unregisterVideo: (id: string) => void;
    setPlaying: (id: string | null) => void;
    setVisiblePostId: (id: string | null) => void;
}

const VideoContext = createContext<VideoContextType | undefined>(undefined);

export function VideoProvider({ children }: { children: React.ReactNode }) {
    const [currentlyPlayingId, setCurrentlyPlayingId] = useState<string | null>(null);
    const [visiblePostId, setVisiblePostIdState] = useState<string | null>(null);
    // Store pause callbacks for each registered video
    const videoCallbacks = useRef<Map<string, () => void>>(new Map());

    const registerVideo = useCallback((id: string, pauseCallback: () => void) => {
        videoCallbacks.current.set(id, pauseCallback);
    }, []);

    const unregisterVideo = useCallback((id: string) => {
        videoCallbacks.current.delete(id);
    }, []);

    const setPlaying = useCallback((id: string | null) => {
        // Pause the currently playing video if it's different
        if (currentlyPlayingId && currentlyPlayingId !== id) {
            const pauseCallback = videoCallbacks.current.get(currentlyPlayingId);
            if (pauseCallback) {
                pauseCallback();
            }
        }
        setCurrentlyPlayingId(id);
    }, [currentlyPlayingId]);

    const setVisiblePostId = useCallback((id: string | null) => {
        setVisiblePostIdState(id);
    }, []);

    return (
        <VideoContext.Provider value={{
            currentlyPlayingId,
            visiblePostId,
            registerVideo,
            unregisterVideo,
            setPlaying,
            setVisiblePostId,
        }}>
            {children}
        </VideoContext.Provider>
    );
}

export function useVideoContext() {
    const context = useContext(VideoContext);
    if (!context) {
        throw new Error('useVideoContext must be used within a VideoProvider');
    }
    return context;
}

// Optional hook that works even outside provider (for gradual adoption)
export function useOptionalVideoContext(): VideoContextType | null {
    return useContext(VideoContext) ?? null;
}
